import {
  type GovernedMeasureIdentity,
  COST_TO_ACHIEVE_MEASURE_IDENTITY,
  GROSS_SYNERGY_MEASURE_IDENTITY,
} from './governed-measure-identity';

/**
 * #5524 — the governed name, unit and wording of every figure the `ma-deal` module emits.
 * The manager resolves a definition for every `answerAnchors.metricKey`; a key with no row
 * projects a unitless metric and the plan validator refuses it. Declaring the metric here is
 * what lets the planner agree on a unit; it creates no value and admits no evidence.
 *
 * Bands never blend: the conservative, base and stretch totals are three separate declared
 * scenarios of one selected promise set, and cost to achieve is a one-time envelope — none is
 * ever summed with another, nor with any realized or modeled figure from any module.
 *
 * #5524 measure identity — every row below declares its governed four-field identity so
 * requested-measure admission (#5662/#5692) can prove rather than assume compatibility.
 * None of these figures is an operating-expense, operating-income or labor-cost P&L line.
 * #5714 Child 1 added named `gross_synergy` and `cost_to_achieve` dimensions to
 * `GovernedMeasuredDimension` for exactly the two rows that have a real home outside the
 * three-bucket P&L taxonomy but are not generic deal-tracking figures; the remaining
 * realization-tracking figures (realized/promised/variance-to-date) and the four cardinality
 * counts stay `not_applicable` — they are deal-tracking amounts with no P&L-bucket home and
 * no dedicated dimension of their own. No row ever uses `other` (which the admission logic
 * treats as a permanent catch-all mismatch — see `manager-requested-measure.ts` — and so can
 * never itself reach a non-`unverified` verdict).
 */
const MA_DEAL_CURRENCY_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'not_applicable',
  valueForm: 'amount',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};

// The two measurement-binding counts and the off-track-commitment count are cardinalities
// of the selected promise set, not currency figures at all — `not_applicable` (never a
// P&L bucket) with `valueForm: 'count'` and `unitFamily: 'count'` is the only honest
// declaration; there is nothing to normalize (`normalizationBasis: 'none'`).
const MA_DEAL_COUNT_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'not_applicable',
  valueForm: 'count',
  unitFamily: 'count',
  normalizationBasis: 'none',
};

// ma_deal_variance_to_date_usd is banked value MINUS the promise-curve value at the same
// assessment date — a difference of two currency levels, not a level itself. valueForm
// must be 'change', not 'amount' (#5524 review fix); unitFamily/normalizationBasis are
// unchanged because the result is still a currency figure with no normalization basis.
const MA_DEAL_CURRENCY_CHANGE_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'not_applicable',
  valueForm: 'change',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};

const MA_DEAL_MONETARY_SIBLINGS = Object.freeze([
  'ma_deal_gross_synergy_conservative_usd',
  'ma_deal_gross_synergy_base_usd',
  'ma_deal_gross_synergy_stretch_usd',
  'ma_deal_gross_synergy_conservative_committed_usd',
  'ma_deal_gross_synergy_conservative_modeled_usd',
  'ma_deal_gross_synergy_base_committed_usd',
  'ma_deal_gross_synergy_base_modeled_usd',
  'ma_deal_gross_synergy_stretch_committed_usd',
  'ma_deal_gross_synergy_stretch_modeled_usd',
  'ma_deal_cost_to_achieve_usd',
  'ma_deal_realized_to_date_usd',
  'ma_deal_promised_to_date_usd',
  'ma_deal_current_plan_to_date_usd',
  'ma_deal_variance_to_date_usd',
  'ma_deal_variance_to_plan_usd',
  // #5714 Child 1 static-family detail figures (per-commitment, not the aggregate band/total
  // above) — see ma-deal.metric-keys.ts MA_DEAL_COMMITMENT_TARGET_METRIC_FAMILY /
  // MA_DEAL_COMMITMENT_COST_TO_ACHIEVE_METRIC_FAMILY.
  'ma_deal_commitment_target_usd',
  'ma_deal_commitment_cost_to_achieve_usd',
  // #7251 — the allocation drill-down's per-node/per-band static family — see
  // ma-deal.metric-keys.ts MA_DEAL_ALLOCATION_TARGET_METRIC_FAMILY.
  'ma_deal_allocation_target_usd',
] as const);

const MA_DEAL_EVIDENCE_CLASSES = Object.freeze(['ma_deal_case_pack_records'] as const);

export interface MaDealMetricRegistrySeedInput {
  readonly metricKey: string;
  readonly label: string;
  readonly registryLayer: 'core';
  readonly appliesTo: readonly [];
  readonly aggregationRules: readonly [];
  readonly blendPolicy: Readonly<{ kind: 'never_blend' }>;
  readonly benchmarkable: false;
  readonly sourceOfTruthClass: 'derived';
  readonly ownerModuleId: 'ma-deal';
  readonly sourceBindingKeys: readonly string[];
  readonly requiredEvidenceClasses: typeof MA_DEAL_EVIDENCE_CLASSES;
  readonly neverSumWith: readonly (typeof MA_DEAL_MONETARY_SIBLINGS)[number][];
  readonly unit: string;
  readonly temporalType: 'flow' | 'ratio' | 'count';
  readonly direction: 'higher_is_better' | 'lower_is_better' | 'context';
  readonly definition: string;
  readonly reconciliationRule: string;
  readonly source: string;
  readonly measureIdentity: GovernedMeasureIdentity;
}

const SOURCE = 'apps/api/src/decision-modules/ma-deal/ma-deal.read-case.figures.ts';
const REALIZATION_SOURCE = 'apps/api/src/decision-modules/ma-deal/ma-deal.module-definition.ts';
const MEASUREMENT_COUNT_SOURCE =
  'apps/api/src/decision-modules/ma-deal/ma-deal.module-definition.ts';
const ALLOCATION_SOURCE = 'apps/api/src/decision-modules/ma-deal/ma-deal.allocation-drilldown.ts';

function bandRow(
  band: 'conservative' | 'base' | 'stretch',
  certainty?: 'committed' | 'modeled',
): MaDealMetricRegistrySeedInput {
  const metricKey = `ma_deal_gross_synergy_${band}${certainty ? `_${certainty}` : ''}_usd`;
  const classLabel = certainty === 'committed'
    ? 'C1 committed'
    : certainty === 'modeled'
      ? 'C2 modeled upside'
      : null;
  return {
    metricKey,
    label: classLabel
      ? `${classLabel} share of underwritten gross synergy, ${band} band`
      : `Underwritten gross synergy, ${band} band (selected deal promise set)`,
    definition: classLabel
      ? `The ${band}-band portion of the approved acquisition promise set carried by ${classLabel} commitments. Certainty is post-approval realization governance and does not alter promise-set membership.`
      : `What was underwritten for the acquisition at the ${band} band: the sum of the declared ${band}-band targets of every gross synergy-benefit commitment in the admitted deal's selected promise set, on the promise set's value basis.`,
    reconciliationRule: classLabel
      ? 'Partition every approved gross-synergy commitment exactly once by certainty class; committed plus modeled must equal the underwritten band total.'
      : 'Sum one declared band only from the selected promise set; never mix bands or value bases, and never add an observed actual to a promise.',
    unit: 'USD',
    temporalType: 'flow',
    source: `${SOURCE}#metricKey:${metricKey}`,
    registryLayer: 'core',
    appliesTo: [],
    aggregationRules: [],
    blendPolicy: { kind: 'never_blend' },
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'ma-deal',
    sourceBindingKeys: [metricKey],
    requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES,
    neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== metricKey),
    direction: 'higher_is_better',
    // #5714 Child 1 — gross-synergy bands (target and both certainty-class shares) are the
    // named `gross_synergy` dimension, not the placeholder `not_applicable` this file used
    // before the ticket's closed semantic additions landed.
    measureIdentity: GROSS_SYNERGY_MEASURE_IDENTITY,
  };
}

function countRow(
  metricKey: string,
  label: string,
  definition: string,
): MaDealMetricRegistrySeedInput {
  return {
    metricKey,
    label,
    definition,
    reconciliationRule: 'Count each commitment in the selected promise set exactly once according to whether its referenced measurement-binding record is present.',
    unit: 'count',
    temporalType: 'count',
    source: `${MEASUREMENT_COUNT_SOURCE}#metricKey:${metricKey}`,
    registryLayer: 'core',
    appliesTo: [],
    aggregationRules: [],
    blendPolicy: { kind: 'never_blend' },
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'ma-deal',
    sourceBindingKeys: [metricKey],
    requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES,
    neverSumWith: [],
    direction: 'context',
    measureIdentity: MA_DEAL_COUNT_MEASURE_IDENTITY,
  };
}

export const MA_DEAL_METRIC_REGISTRY_SEED_INPUTS: readonly MaDealMetricRegistrySeedInput[] = Object.freeze([
  bandRow('conservative'),
  bandRow('base'),
  bandRow('stretch'),
  bandRow('conservative', 'committed'),
  bandRow('conservative', 'modeled'),
  bandRow('base', 'committed'),
  bandRow('base', 'modeled'),
  bandRow('stretch', 'committed'),
  bandRow('stretch', 'modeled'),
  {
    metricKey: 'ma_deal_cost_to_achieve_usd',
    label: 'M&A deal case: selected cost to achieve',
    definition: 'Sum of the declared one-time cost-to-achieve envelope across every commitment in the admitted deal\'s selected promise set.',
    reconciliationRule: 'Sum the declared cost-to-achieve of every commitment in the selected promise set; never net it against synergy targets.',
    unit: 'USD',
    temporalType: 'flow',
    source: `${SOURCE}#metricKey:ma_deal_cost_to_achieve_usd`,
    registryLayer: 'core',
    appliesTo: [],
    aggregationRules: [],
    blendPolicy: { kind: 'never_blend' },
    benchmarkable: false,
    sourceOfTruthClass: 'derived',
    ownerModuleId: 'ma-deal',
    sourceBindingKeys: ['ma_deal_cost_to_achieve_usd'],
    requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES,
    neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_cost_to_achieve_usd'),
    direction: 'lower_is_better',
    // #5714 Child 1 — the named `cost_to_achieve` dimension, per the ticket's exact identity
    // mapping (never `gross_synergy`, never `not_applicable`: a one-time envelope is neither).
    measureIdentity: COST_TO_ACHIEVE_MEASURE_IDENTITY,
  },
  countRow(
    'ma_deal_measurement_binding_present_count',
    'M&A deal commitments with a declared measurement binding',
    'Count of commitments in the selected promise set whose referenced measurement-binding record is present. Validation and evidence posture remain separate structured facts.',
  ),
  countRow(
    'ma_deal_measurement_binding_absent_count',
    'M&A deal commitments without a declared measurement binding',
    'Count of commitments in the selected promise set whose referenced measurement-binding record is absent.',
  ),
  {
    metricKey: 'ma_deal_realized_to_date_usd', label: 'M&A commitment realization: banked to date', definition: 'The cumulative banked value at the assessment date for one governed commitment, read from its attested actual series.', unit: 'USD', temporalType: 'flow', direction: 'higher_is_better',
    reconciliationRule: 'Read one commitment at one assessment date; preserve promise, plan, actual and forecast as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_realized_to_date_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_realized_to_date_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_realized_to_date_usd'),
    measureIdentity: MA_DEAL_CURRENCY_MEASURE_IDENTITY,
  },
  {
    metricKey: 'ma_deal_promised_to_date_usd', label: 'M&A commitment realization: promised to date', definition: 'The approved original-promise curve value at the assessment date for one governed commitment.', unit: 'USD', temporalType: 'flow', direction: 'context',
    reconciliationRule: 'Read one commitment at one assessment date; preserve promise, plan, actual and forecast as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_promised_to_date_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_promised_to_date_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_promised_to_date_usd'),
    measureIdentity: MA_DEAL_CURRENCY_MEASURE_IDENTITY,
  },
  {
    metricKey: 'ma_deal_current_plan_to_date_usd', label: 'M&A commitment realization: current plan to date', definition: 'The selected current-plan operand at or before the assessment cutoff for one governed commitment; preserve its recorded observation date and value basis.', unit: 'USD', temporalType: 'flow', direction: 'context',
    reconciliationRule: 'Read one selected current-plan operand; preserve original promise, plan and actual as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_current_plan_to_date_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_current_plan_to_date_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_current_plan_to_date_usd'),
    measureIdentity: MA_DEAL_CURRENCY_MEASURE_IDENTITY,
  },
  {
    metricKey: 'ma_deal_variance_to_date_usd', label: 'M&A commitment realization: variance to date', definition: 'Banked value minus the approved original-promise curve value at the same assessment date for one governed commitment.', unit: 'USD', temporalType: 'flow', direction: 'higher_is_better',
    reconciliationRule: 'Read one commitment at one assessment date; preserve promise, plan, actual and forecast as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_variance_to_date_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_variance_to_date_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_variance_to_date_usd'),
    measureIdentity: MA_DEAL_CURRENCY_CHANGE_MEASURE_IDENTITY,
  },
  {
    metricKey: 'ma_deal_variance_to_plan_usd', label: 'M&A commitment realization: variance against current plan', definition: 'Latest eligible banked value minus the latest eligible current-plan value at the assessment cutoff for one governed commitment; preserve both observation dates.', unit: 'USD', temporalType: 'flow', direction: 'higher_is_better',
    reconciliationRule: 'Read one commitment at one assessment date; preserve promise, plan, actual and forecast as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_variance_to_plan_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_variance_to_plan_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_variance_to_plan_usd'),
    measureIdentity: MA_DEAL_CURRENCY_CHANGE_MEASURE_IDENTITY,
  },
  {
    metricKey: 'ma_deal_commitments_off_track_count', label: 'M&A commitment realization: off-track commitment count', definition: 'Count of individually assessed governed commitments whose four-series delivery state is off track.', unit: 'count', temporalType: 'count', direction: 'lower_is_better',
    reconciliationRule: 'Read one commitment at one assessment date; preserve promise, plan, actual and forecast as separate authorities and never sum one series with another.',
    source: `${REALIZATION_SOURCE}#metricKey:ma_deal_commitments_off_track_count`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_commitments_off_track_count'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: [],
    measureIdentity: MA_DEAL_COUNT_MEASURE_IDENTITY,
  },
  {
    // #5714 Child 1 static-family closure (#5850 micro-gate HOLD, comment 5475662406):
    // maDealComponentTargetFigures (ma-deal.read-case.figures.ts) calls componentTargetFigure
    // for EVERY commitment in the promise set, unfiltered by commitment type -- not just
    // isGrossSynergyBenefit ones. DealCommitmentV1.commitmentType is a closed six-value
    // vocabulary (packages/lib/src/ma-deal-case.ts): synergy_benefit,
    // dis_synergy_or_leakage, cost_to_achieve, standalone_performance,
    // retention_or_asset_protection, strategic_outcome. GROSS_SYNERGY_MEASURE_IDENTITY was
    // wrong here -- true only for the synergy_benefit rows this family emits, a false
    // declaration for the other five commitment types.
    //
    // The #5714 amendment's own MaDealCommitmentMetricFamilyV1 type
    // (issuecomment-5473888431) defines exactly ONE closed target family --
    // 'ma_deal_commitment_target_usd' -- with no per-commitment-class split, and states
    // explicitly "CTA is not treated as gross synergy." Splitting this family by commitment
    // class would add family values the ticket never defined; the ticket's own text instead
    // requires the ONE family to declare the dimension it actually guarantees for every row
    // it emits. No single P&L-bucket dimension (gross_synergy, cost_to_achieve, or any of the
    // ticket's other four additions) is true across all six commitment types, so this takes
    // MA_DEAL_CURRENCY_MEASURE_IDENTITY (not_applicable/amount/currency/none) -- the same
    // declaration this file already uses for ma_deal_realized_to_date_usd /
    // ma_deal_promised_to_date_usd, the other generic deal-tracking amounts in this file with
    // "no P&L-bucket home and no dedicated dimension of their own" (see this file's header
    // comment). direction also corrected to 'context' for the same reason
    // ma_deal_promised_to_date_usd uses 'context': a declared target is a reference value, not
    // something with a universal higher/lower-is-better sense across dis-synergy, cost and
    // retention commitments alongside synergy-benefit ones.
    metricKey: 'ma_deal_commitment_target_usd', label: 'M&A deal case: one commitment\'s band target', definition: 'The governed band target for one modeled underwriting component (commitment) of any commitment type in the admitted deal\'s selected promise set, on its declared value basis.', unit: 'USD', temporalType: 'flow', direction: 'context',
    reconciliationRule: 'Publish the component target directly; never aggregate a non-coherent modeled promise set.',
    source: `${SOURCE}#metricKey:ma_deal_commitment_target_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_commitment_target_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_commitment_target_usd'),
    measureIdentity: MA_DEAL_CURRENCY_MEASURE_IDENTITY,
  },
  {
    // Same closure as above, for componentCostFigure (ma-deal.read-case.figures.ts:181).
    // Same governed dimension as the aggregate ma_deal_cost_to_achieve_usd envelope, just at
    // per-commitment grain, so it takes the identical COST_TO_ACHIEVE_MEASURE_IDENTITY.
    metricKey: 'ma_deal_commitment_cost_to_achieve_usd', label: 'M&A deal case: one commitment\'s cost to achieve', definition: 'The governed one-time cost to achieve for one modeled underwriting component (commitment) in the admitted deal\'s selected promise set, on its declared value basis.', unit: 'USD', temporalType: 'flow', direction: 'lower_is_better',
    reconciliationRule: 'Publish the component cost directly; never aggregate a non-coherent modeled promise set.',
    source: `${SOURCE}#metricKey:ma_deal_commitment_cost_to_achieve_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_commitment_cost_to_achieve_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_commitment_cost_to_achieve_usd'),
    measureIdentity: COST_TO_ACHIEVE_MEASURE_IDENTITY,
  },
  {
    // #7251 — the allocation drill-down's per-node/per-band target figure. Same closure as the
    // two component-detail rows above: a selected header's nodes span both leaf and subtotal,
    // retire and retain dispositions, across whichever scenario was selected, so no single
    // P&L-bucket dimension is true for every row this family emits. Takes
    // MA_DEAL_CURRENCY_MEASURE_IDENTITY and direction 'context', matching
    // ma_deal_commitment_target_usd above. Node/band/scenario identity travels as the
    // NumericBinding placeholder and typed facts (ma-deal.allocation-drilldown.ts), never in
    // this metric key.
    metricKey: 'ma_deal_allocation_target_usd', label: 'M&A deal case: one allocation node\'s band target', definition: 'The governed band target for one node of a selected commitment allocation-scenario drill-down, on the header\'s declared value basis.', unit: 'USD', temporalType: 'flow', direction: 'context',
    reconciliationRule: 'Publish the allocation node target directly; never sum across scenarios.',
    source: `${ALLOCATION_SOURCE}#metricKey:ma_deal_allocation_target_usd`, registryLayer: 'core', appliesTo: [], aggregationRules: [], blendPolicy: { kind: 'never_blend' }, benchmarkable: false, sourceOfTruthClass: 'derived', ownerModuleId: 'ma-deal', sourceBindingKeys: ['ma_deal_allocation_target_usd'], requiredEvidenceClasses: MA_DEAL_EVIDENCE_CLASSES, neverSumWith: MA_DEAL_MONETARY_SIBLINGS.filter((key) => key !== 'ma_deal_allocation_target_usd'),
    measureIdentity: MA_DEAL_CURRENCY_MEASURE_IDENTITY,
  },
]);
